import type { CSSObject } from "@emotion/serialize";
import styled from "@emotion/styled";
import { margin, padding } from "polished";
import React, { PropsWithChildren } from "react";

import { BreakPointVals } from "@brexhq/shared/styles/breakpoints";
import { defaultTheme } from "@brexhq/shared/styles/theming";
import { ColumnProps } from "@brexhq/shared/types/Grid";
import { SpanAndPosition } from "@brexhq/shared/types/SpanAndPosition";

const getColumnRules = (values: ColumnProps["LRG"]): CSSObject => {
  return values === "hidden"
    ? {
        display: "none",
        visibility: "hidden",
      }
    : {
        gridColumnStart: values.start ?? "auto",
        gridColumnEnd: values.end,
        gridRow: values.row ?? "auto",
        // Need these to overwrite the breakpoints when using hidden prop
        display: "initial",
        visibility: "visible",
      };
};

export const getSpanAndPositionColumn = (
  spanAndPosition: SpanAndPosition,
): ColumnProps => {
  return {
    LRG: {
      start: spanAndPosition.desktopPosition,
      end: `span ${spanAndPosition.desktopSpan}`,
      row: 1,
    },
    MED: {
      start: spanAndPosition.tabletPosition,
      end: `span ${spanAndPosition.tabletSpan}`,
      row: 1,
    },
    SML: {
      ...(spanAndPosition.mobileFlush
        ? { start: 1, end: "span 4", row: 1 }
        : {
            start: spanAndPosition.mobilePosition || 1,
            end: `span ${spanAndPosition.mobileSpan || 4}`,
            row: 1,
          }),
    },
  } as ColumnProps;
};

/**
 * returns styling that would reverse the margins that are added in the grid,
 * allowing you to get elements full screen while still using the grid.
 * @param breakpoint one of BreakPoint
 */
export const getMarginReverseByBreakpoint = (
  breakpoint: BreakPointVals | "pastGrid",
) => {
  if (breakpoint === "pastGrid") {
    return `calc(((100vw - ${defaultTheme.maxWidths.moduleGrid}) * -.5 ) - ${defaultTheme.grid.wrapperOuterMargin.LRG}px )`;
  }
  return `-${defaultTheme.grid.wrapperOuterMargin[breakpoint]}px`;
};

export const gridOuterPadding = {
  LRG: 72,
  MED: 48,
  SML: 24,
};

/**
 * Grid column, its children can be anything
 */
export const Column = styled.div<ColumnProps>(
  ({ theme: { breakpoints }, LRG, MED, SML }) => ({
    ...getColumnRules(LRG),
    [breakpoints.MED]: {
      ...getColumnRules(MED),
    },
    [breakpoints.SML]: {
      ...getColumnRules(SML),
    },
  }),
);

export const isFullWidthColumn: ColumnProps = {
  LRG: { end: "span 24" },
  MED: { end: "span 12" },
  SML: { end: "span 4" },
};

export const GridWrapper = styled.div(({ theme }) => ({
  width: `min(100%, ${theme.maxWidths.moduleGrid})`,
  ...margin(0, "auto"),
  position: "relative",
}));

/**
 * Useful if needing to build your own backgrounds eg for an image
 */
export const UnwrappedGrid = styled.div(({ theme: { breakpoints } }) => ({
  display: "grid",
  ...padding(0, gridOuterPadding.LRG),
  gridTemplateColumns: "repeat(24, 1fr)",
  [breakpoints.MED]: {
    ...padding(0, gridOuterPadding.MED),
    gridTemplateColumns: "repeat(12, 1fr)",
  },
  [breakpoints.SML]: {
    ...padding(0, gridOuterPadding.SML),
    gridTemplateColumns: "repeat(4, 1fr)",
  },
}));

/**
 * Useful if needing a module to "snap" to the grid but you dont need columns
 */
export const GridWidth: React.FC<PropsWithChildren> = ({ children }) => (
  <Grid>
    <Column {...isFullWidthColumn}>{children}</Column>
  </Grid>
);

/**
 * Standard grid, handling max width and outer spacing its children should only be columns
 */
export const Grid: React.FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => (
  <GridWrapper className={className}>
    <UnwrappedGrid>{children}</UnwrappedGrid>
  </GridWrapper>
);

/**
 * Full-width grid that removes all constraints for edge-to-edge layouts
 */
export const FullWidthGrid = styled.div<{ removePadding?: boolean }>(
  ({ removePadding }) => ({
    display: "grid",
    width: "100%",
    maxWidth: "none",
    margin: 0,
    padding: removePadding ? 0 : undefined,
    gridTemplateColumns: "repeat(24, 1fr)",
    [`@media (max-width: ${defaultTheme.breakpoints.MED})`]: {
      gridTemplateColumns: "repeat(12, 1fr)",
    },
    [`@media (max-width: ${defaultTheme.breakpoints.SML})`]: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  }),
);

/**
 * Full-width grid wrapper that bypasses the standard GridWrapper constraints
 */
export const FullWidthGridWrapper = styled.div<{ removePadding?: boolean }>(
  ({ removePadding }) => ({
    width: "100%",
    maxWidth: "none",
    margin: 0,
    padding: removePadding ? 0 : undefined,
    position: "relative",
  }),
);

/**
 * Utility function to create full-width column props
 */
export const getFullWidthColumnProps = (): ColumnProps => ({
  LRG: { start: 1, end: "span 24" },
  MED: { start: 1, end: "span 12" },
  SML: { start: 1, end: "span 4" },
});
